import { supabase } from "@/lib/supabase";
import { getEdgeFunctionErrorMessage } from "@/lib/edgeFunctionErrorMessage";
import { fetchWithRetry } from "@/lib/network/fetchWithRetry";
import {
  getSupabaseEdgeFunctionUrl,
  getSupabaseFunctionsBaseUrl,
  warnIfSupabaseDatabaseAndFunctionsHostsMismatch,
} from "@/lib/supabaseFunctionsUrl";

export interface InvokeEdgeFunctionOptions {
  body?: unknown;
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

/**
 * Invokes a Supabase Edge Function with the same JWT + apikey semantics as {@link supabase.functions.invoke},
 * but resolves the base URL via {@link getSupabaseEdgeFunctionUrl} so `VITE_SUPABASE_FUNCTIONS_URL` applies.
 *
 * `createClient(VITE_SUPABASE_URL)` always targets `{url}/functions/v1`, which breaks when the DB/API use one
 * host (e.g. local `127.0.0.1:54321`) and deployed functions live on another (`https://…supabase.co`).
 */
export async function invokeSupabaseEdgeFunction<T>(
  functionName: string,
  options: InvokeEdgeFunctionOptions = {},
): Promise<{ data: T | null; error: Error | null }> {
  warnIfSupabaseDatabaseAndFunctionsHostsMismatch();
  const url = getSupabaseEdgeFunctionUrl(functionName);
  const anonKey = (import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined)?.trim() ?? "";

  if (!url) {
    return {
      data: null,
      error: new Error(
        "Missing Supabase URL. Set VITE_SUPABASE_URL, or VITE_SUPABASE_FUNCTIONS_URL for Edge Functions only.",
      ),
    };
  }

  if (import.meta.env.DEV) {
    const base = getSupabaseFunctionsBaseUrl();
    const primary = (import.meta.env.VITE_SUPABASE_URL as string | undefined)?.trim();
    if (
      base &&
      primary &&
      base.replace(/\/$/, "") !== primary.replace(/\/$/, "") &&
      (import.meta.env.VITE_SUPABASE_FUNCTIONS_URL as string | undefined)?.trim()
    ) {
      console.debug("[invokeSupabaseEdgeFunction] using VITE_SUPABASE_FUNCTIONS_URL for", functionName);
    }
  }

  const { data: sessionData } = await supabase.auth.getSession();
  const accessToken = sessionData.session?.access_token;

  const headers: Record<string, string> = {
    apikey: anonKey,
    Authorization: `Bearer ${accessToken ?? anonKey}`,
    "Content-Type": "application/json",
  };

  let response: Response;
  try {
    response = await fetchWithRetry(url, {
      method: "POST",
      headers,
      body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
      maxAttempts: 3,
      timeoutMs: 45_000,
    });
  } catch (e) {
    const err = e instanceof Error ? e : new Error(String(e));
    if (import.meta.env.DEV) {
      console.error("[invokeSupabaseEdgeFunction] fetch failed", functionName, url, err);
    }
    return { data: null, error: err };
  }

  const ct = response.headers.get("Content-Type") ?? "";
  let payload: unknown = null;
  if (ct.includes("application/json")) {
    try {
      payload = await response.json();
    } catch {
      payload = null;
    }
  } else {
    const text = await response.text();
    try {
      payload = text ? JSON.parse(text) : null;
    } catch {
      payload = text;
    }
  }

  if (!response.ok) {
    const fallback =
      isRecord(payload) && typeof payload.message === "string"
        ? payload.message
        : `HTTP ${response.status}`;
    const msg = getEdgeFunctionErrorMessage(new Error(fallback), payload);
    return { data: payload as T, error: new Error(msg) };
  }

  return { data: payload as T, error: null };
}
